
import { defineComponent, nextTick } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { settingsdata } from "@/api/settings";
import {
  createContentCampaign,
  getContentCampaign,
  getContentSettings,
  updateContentCampaign
} from "@/api/ghlContent.api.js";
import SocialPostPreview from "@/components/social/SocialPostPreview.vue";
import ContentMediaLibrary from "@/components/social/ContentMediaLibrary.vue";
import SuggestCaptionDialog from "@/components/social/SuggestCaptionDialog.vue";
import SuggestPostImageDialog from "@/components/social/SuggestPostImageDialog.vue";
import {
  accountDisplayName,
  accountOptionKey,
  getPlatformMeta,
  normalizePlatform,
  uniquePlatformsFromAccounts
} from "@/utils/socialPlatformMeta.js";
import {
  MAX_FOLLOW_UP_COMMENT_LENGTH,
  contentHasVideo,
  facebookPostTypeRequiresVideo,
  hasPendingVideoFiles
} from "@/utils/socialPostPolicy.js";

type PendingFile = {
  file: File;
  previewUrl: string;
  isImage: boolean;
};

export default defineComponent({
  name: "team-content-publish",
  components: {
    SocialPostPreview,
    ContentMediaLibrary,
    SuggestCaptionDialog,
    SuggestPostImageDialog
  },
  props: {
    embedded: {
      type: Boolean,
      default: false
    },
    campaignId: {
      type: Number,
      default: 0
    }
  },
  emits: ["view-history"],
  data() {
    return {
      loading: true,
      publishing: false,
      saving: false,
      isTeamLeader: false,
      hasGhlLocation: false,
      connectedAccounts: [] as any[],
      selectedAccountKeys: [] as string[],
      mediaUrlsText: "",
      scheduleDialogVisible: false,
      suggestCaptionVisible: false,
      suggestCaptionMode: "draft" as "draft" | "rewrite",
      suggestPostImageVisible: false,
      editingCampaignId: 0,
      campaignStatus: "",
      pendingFiles: [] as PendingFile[],
      attachedMediaIds: [] as number[],
      attachedPreviewUrls: [] as string[],
      pollTimer: null as ReturnType<typeof setInterval> | null,
      facebookOptionsOpen: true,
      maxFollowUpCommentLength: MAX_FOLLOW_UP_COMMENT_LENGTH,
      facebookPostTypeOptions: [
        { value: "post", label: "Feed" },
        { value: "reel", label: "Reel" },
        { value: "story", label: "Story" }
      ],
      form: {
        caption: "",
        linkUrl: "",
        followUpComment: "",
        facebookPostType: "post",
        platforms: [] as string[],
        scheduledAt: "",
        cascadeToTeam: false,
        varyCaption: false
      },
      lastResult: null as any
    };
  },
  computed: {
    pageTitle(): string {
      return this.editingCampaignId ? "Edit Draft" : "New Social Post";
    },
    selectedAccounts(): any[] {
      return this.connectedAccounts.filter((account) =>
        this.selectedAccountKeys.includes(accountOptionKey(account))
      );
    },
    mediaUrls(): string[] {
      return this.mediaUrlsText
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    },
    previewMediaUrls(): string[] {
      const pending = this.pendingFiles.map((item) => item.previewUrl).filter(Boolean);
      return [...this.attachedPreviewUrls, ...pending, ...this.mediaUrls];
    },
    footerDisabled(): boolean {
      return !this.hasGhlLocation || !this.selectedAccountKeys.length || this.publishing || this.saving || this.campaignStatus === "preparing";
    },
    postButtonLabel(): string {
      return "Post";
    },
    followUpCommentLength(): number {
      return this.form.followUpComment.length;
    },
    hasFacebookSelected(): boolean {
      return this.selectedAccounts.some(
        (account) => normalizePlatform(account.platform) === "facebook"
      );
    },
    facebookPostTypeHelp(): string {
      if (this.form.facebookPostType === "reel") {
        return "Reels are short vertical videos. Attach a video before publishing.";
      }
      if (this.form.facebookPostType === "story") {
        return "Stories disappear after 24 hours. Attach a video before publishing.";
      }
      return "Standard feed post with text, images, or links.";
    },
    previewHasVideo(): boolean {
      return hasPendingVideoFiles(this.pendingFiles)
        || contentHasVideo(this.previewMediaUrls);
    }
  },
  async mounted() {
    await Promise.all([this.loadProfile(), this.loadContentSettings()]);
    this.applyRoutePrefill();
    await this.loadDraftIfNeeded();
    this.loading = false;
  },
  beforeUnmount() {
    this.stopPolling();
    this.revokePendingPreviews();
  },
  watch: {
    "$route.query": {
      deep: true,
      async handler() {
        this.applyRoutePrefill();
        await this.loadDraftIfNeeded();
      }
    },
    campaignId: {
      immediate: false,
      async handler(value: number) {
        if (value) {
          await this.loadDraft(value);
        }
      }
    },
    attachedMediaIds: {
      deep: true,
      handler() {
        this.syncAttachedPreviewUrls();
      }
    }
  },
  methods: {
    getPlatformMeta,
    accountDisplayName,
    accountOptionKey,
    isPreviewImage(url: string) {
      return url.startsWith("blob:") || /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(url);
    },
    async loadProfile() {
      const response = await settingsdata();
      this.isTeamLeader = Boolean(response.data?.isTeamLeader);
    },
    async loadContentSettings() {
      try {
        const response = await getContentSettings();
        const data = response.data || {};
        this.hasGhlLocation = Boolean(data.hasGhlLocation);
        const accounts = Array.isArray(data.socialAccounts) ? data.socialAccounts : [];
        this.connectedAccounts = accounts.filter(
          (account: any) => account.status !== "disconnected"
        );
      } catch {
        this.hasGhlLocation = false;
        this.connectedAccounts = [];
      }
    },
    onAccountsChange() {
      this.form.platforms = uniquePlatformsFromAccounts(this.selectedAccounts);
    },
    applyRoutePrefill() {
      const query = this.$route.query || {};
      if (query.caption) this.form.caption = String(query.caption);
      if (query.linkUrl) this.form.linkUrl = String(query.linkUrl);
      if (query.mediaUrl) this.mediaUrlsText = String(query.mediaUrl);
      if (query.name && !this.form.caption) {
        this.form.caption = String(query.name);
      }
    },
    resolveCampaignId() {
      if (this.campaignId) return this.campaignId;
      const queryId = Number(this.$route.query.campaignId || 0);
      return queryId || this.editingCampaignId;
    },
    async loadDraftIfNeeded() {
      const id = this.resolveCampaignId();
      if (id) {
        await this.loadDraft(id);
      }
    },
    async loadDraft(id: number) {
      try {
        const response = await getContentCampaign(id);
        const campaign = response.data || response;
        if (!campaign?.id) return;

        this.editingCampaignId = campaign.id;
        this.campaignStatus = campaign.status || "";
        const content = campaign.content || {};
        this.form.caption = content.caption || "";
        this.form.linkUrl = content.linkUrl || "";
        this.form.followUpComment = content.followUpComment || "";
        this.form.facebookPostType = content.facebookPostType || "post";
        this.mediaUrlsText = (content.mediaUrls || []).join("\n");
        this.attachedMediaIds = Array.isArray(content.mediaIds) ? [...content.mediaIds] : [];
        this.form.cascadeToTeam = Boolean(campaign.teamId);
        this.form.varyCaption = Boolean(content.varyCaption);
        if (campaign.scheduledAt) {
          const local = new Date(campaign.scheduledAt);
          const offset = local.getTimezoneOffset();
          const adjusted = new Date(local.getTime() - offset * 60000);
          this.form.scheduledAt = adjusted.toISOString().slice(0, 16);
        } else {
          this.form.scheduledAt = "";
        }

        this.syncAccountsFromPlatforms(campaign.platforms || []);
        await nextTick();
        this.syncAttachedPreviewUrls();

        if (this.campaignStatus === "preparing") {
          this.startPolling(campaign.id);
        }
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to load draft.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      }
    },
    syncAccountsFromPlatforms(platforms: string[]) {
      const normalized = new Set((platforms || []).map((p) => String(p).toLowerCase()));
      this.selectedAccountKeys = this.connectedAccounts
        .filter((account) => normalized.has(String(account.platform || "").toLowerCase()))
        .map((account) => accountOptionKey(account));
      this.form.platforms = uniquePlatformsFromAccounts(this.selectedAccounts);
    },
    syncAttachedPreviewUrls() {
      const library = this.$refs.mediaLibrary as any;
      if (!library) return;
      this.attachedPreviewUrls = this.attachedMediaIds
        .map((id) => library.getPublicUrlForId(id))
        .filter(Boolean);
    },
    onMediaLibraryRefresh() {
      this.syncAttachedPreviewUrls();
    },
    viewHistory() {
      if (this.embedded) {
        this.$emit("view-history");
        return;
      }
      this.$router.push({ path: "/settings/ai247-portal", query: { tab: "history" } });
    },
    openSuggestCaption(mode: "draft" | "rewrite") {
      if (!this.hasGhlLocation) return;
      if (!this.selectedAccountKeys.length) {
        Swal.fire({
          icon: "info",
          title: "Select accounts first",
          text: "Choose at least one social account under Post to before suggesting a caption.",
          confirmButtonText: "OK"
        });
        return;
      }
      this.suggestCaptionMode = mode;
      this.suggestCaptionVisible = true;
    },
    openSuggestPostImage() {
      if (!this.hasGhlLocation) return;
      if (!this.selectedAccountKeys.length) {
        Swal.fire({
          icon: "info",
          title: "Select accounts first",
          text: "Choose at least one social account under Post to before suggesting an image.",
          confirmButtonText: "OK"
        });
        return;
      }
      this.suggestPostImageVisible = true;
    },
    applySuggestedImage(payload: { b64Json?: string; mimeType?: string }) {
      const b64Json = String(payload?.b64Json || "").trim();
      const mimeType = String(payload?.mimeType || "image/png");
      if (!b64Json) return;

      const binary = atob(b64Json);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const extension = mimeType.includes("jpeg") ? "jpg" : "png";
      const file = new File([bytes], `suggest-post-image.${extension}`, { type: mimeType });
      this.pendingFiles.push({
        file,
        previewUrl: URL.createObjectURL(file),
        isImage: true
      });
    },
    applySuggestedCaption(text: string) {
      const nextCaption = String(text || "").trim();
      if (!nextCaption) return;
      if (
        this.suggestCaptionMode !== "rewrite"
        && this.form.caption.trim()
        && this.form.caption.trim() !== nextCaption
      ) {
        Swal.fire({
          icon: "question",
          title: "Replace caption?",
          text: "This will replace the text currently in your post.",
          showCancelButton: true,
          confirmButtonText: "Replace",
          cancelButtonText: "Keep current"
        }).then((result) => {
          if (result.isConfirmed) {
            this.form.caption = nextCaption;
          }
        });
        return;
      }
      this.form.caption = nextCaption;
    },
    triggerFilePicker() {
      const input = this.$refs.fileInput as HTMLInputElement;
      input?.click();
    },
    onFilesSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const files = Array.from(input.files || []);
      for (const file of files) {
        const isImage = file.type.startsWith("image/");
        this.pendingFiles.push({
          file,
          previewUrl: URL.createObjectURL(file),
          isImage
        });
      }
      input.value = "";
    },
    removePendingFile(index: number) {
      const item = this.pendingFiles[index];
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      this.pendingFiles.splice(index, 1);
    },
    revokePendingPreviews() {
      for (const item of this.pendingFiles) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      }
      this.pendingFiles = [];
    },
    buildPayload({ publishNow = true } = {}) {
      const payload: any = {
        content: {
          caption: this.form.caption.trim(),
          mediaUrls: this.mediaUrls,
          linkUrl: this.form.linkUrl.trim() || undefined
        },
        existingMediaIds: this.attachedMediaIds,
        platforms: this.form.platforms,
        scheduledAt: this.form.scheduledAt ? new Date(this.form.scheduledAt).toISOString() : undefined,
        publishNow,
        cascadeToTeam: Boolean(this.form.cascadeToTeam)
      };

      const followUpComment = this.form.followUpComment.trim();
      if (followUpComment) {
        payload.content.followUpComment = followUpComment;
      }
      if (this.hasFacebookSelected) {
        payload.content.facebookPostType = this.form.facebookPostType;
      }
      if (this.form.cascadeToTeam && this.form.varyCaption) {
        payload.content.varyCaption = true;
      }

      return payload;
    },
    validateComposer() {
      if (!this.hasGhlLocation) {
        Swal.fire({
          title: "",
          text: "Connect your AI 24/7 before publishing content.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }
      if (!this.selectedAccountKeys.length) {
        Swal.fire({
          title: "",
          text: "Select at least one social account.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }
      const hasMedia = this.mediaUrls.length > 0
        || this.pendingFiles.length > 0
        || this.attachedMediaIds.length > 0;
      if (!this.form.caption.trim() && !hasMedia) {
        Swal.fire({
          title: "",
          text: "Caption or media is required.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }
      if (!this.form.platforms.length) {
        Swal.fire({
          title: "",
          text: "Select at least one social account.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }
      if (this.form.followUpComment.length > this.maxFollowUpCommentLength) {
        Swal.fire({
          title: "",
          text: `Follow-up comment must be at most ${this.maxFollowUpCommentLength} characters.`,
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }
      if (
        this.hasFacebookSelected
        && facebookPostTypeRequiresVideo(this.form.facebookPostType)
        && !this.previewHasVideo
      ) {
        Swal.fire({
          title: "",
          text: "Facebook Reels and Stories require a video attachment.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }
      return true;
    },
    async submitCampaign({ publishNow = true } = {}) {
      const payload = this.buildPayload({ publishNow });
      const files = this.pendingFiles.map((item) => item.file);
      const id = this.editingCampaignId;

      if (id) {
        return updateContentCampaign(id, payload, files);
      }
      return createContentCampaign(payload, files);
    },
    handleCampaignResponse(response: any, { publishNow = true } = {}) {
      const data = response.data || {};
      const campaign = data.campaign || {};
      const status = campaign.status || (publishNow ? "queued" : "draft");

      this.editingCampaignId = campaign.id || this.editingCampaignId;
      this.campaignStatus = status;
      const moderation = data.moderation || campaign.moderationResult || {};
      this.lastResult = {
        campaignId: campaign.id,
        status,
        postedCount: data.postedCount || 0,
        skippedCount: data.skippedCount || 0,
        failedCount: data.failedCount || 0,
        moderationSummary: moderation.summary || ""
      };

      this.revokePendingPreviews();

      if (status === "preparing" || status === "pending_review" || status === "awaiting_original") {
        this.startPolling(campaign.id);
      } else {
        this.stopPolling();
      }

      const library = this.$refs.mediaLibrary as any;
      library?.loadMedia?.();

      return { data, status };
    },
    startPolling(campaignId: number) {
      this.stopPolling();
      this.pollTimer = setInterval(async () => {
        try {
          const response = await getContentCampaign(campaignId);
          const campaign = response.data || response;
          this.campaignStatus = campaign.status || "";

          if (campaign.status !== "preparing"
            && campaign.status !== "pending_review"
            && campaign.status !== "awaiting_original") {
            this.stopPolling();
            const content = campaign.content || {};
            this.mediaUrlsText = (content.mediaUrls || []).join("\n");
            this.attachedMediaIds = [];
            this.syncAttachedPreviewUrls();
            if (this.lastResult?.campaignId === campaign.id) {
              this.lastResult = {
                ...this.lastResult,
                status: campaign.status || this.lastResult.status,
                moderationSummary: campaign.moderationResult?.summary || this.lastResult.moderationSummary || "",
                postedCount: campaign.stats?.posted ?? this.lastResult.postedCount,
                skippedCount: campaign.stats?.skipped ?? this.lastResult.skippedCount,
                failedCount: campaign.stats?.failed ?? this.lastResult.failedCount
              };
            }
          }
        } catch {
          this.stopPolling();
        }
      }, 3000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async saveDraft() {
      if (!this.validateComposer()) return;

      this.saving = true;
      try {
        const response = await this.submitCampaign({ publishNow: false });
        const { status } = this.handleCampaignResponse(response, { publishNow: false });
        const message = status === "preparing"
          ? "Draft saved. Media is uploading in the background."
          : "Draft saved for later.";
        Swal.fire({
          title: "",
          text: message,
          icon: "success",
          confirmButtonClass: "btn btn-secondary"
        });
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to save draft.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.saving = false;
      }
    },
    handlePostMenu(command: string) {
      if (command === "now") {
        this.form.scheduledAt = "";
        this.submitPostNow();
        return;
      }
      if (command === "schedule") {
        this.scheduleDialogVisible = true;
      }
    },
    async submitPostNow() {
      this.form.scheduledAt = "";
      await this.submitPublish(true);
    },
    async submitScheduledPost() {
      if (!this.form.scheduledAt) {
        Swal.fire({
          title: "",
          text: "Choose a schedule date and time.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return;
      }
      this.scheduleDialogVisible = false;
      await this.submitPublish(true);
    },
    async submitPublish(publishNow = true) {
      if (!this.validateComposer()) return;

      this.publishing = true;
      try {
        const response = await this.submitCampaign({ publishNow });
        const { data, status } = this.handleCampaignResponse(response, { publishNow });

        let message = "Draft saved for later.";
        let icon: "success" | "warning" | "error" = "success";
        if (status === "preparing") {
          message = "Post queued. Media is uploading in the background.";
        } else if (status === "rejected") {
          const moderation = data.moderation || data.campaign?.moderationResult || {};
          message = moderation.summary
            ? `Post blocked by AI review: ${moderation.summary}`
            : "Post blocked by AI review against company rules.";
          icon = "error";
        } else if (status === "pending_review") {
          message = "Post is under AI review before team fan-out.";
          icon = "warning";
        } else if (publishNow) {
          message = `Published to ${data.postedCount} account(s). Skipped ${data.skippedCount}. Failed ${data.failedCount}.`;
          icon = data.failedCount ? "warning" : "success";
        }

        Swal.fire({
          title: "",
          text: message,
          icon,
          confirmButtonClass: "btn btn-secondary"
        });
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Publish failed.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.publishing = false;
      }
    }
  }
});
